import React, { useState } from 'react';
import { AssetType, ManualBrokerID } from 'config/consts';
import { useRouting } from 'hooks/useRouting';
import { useScroll } from 'hooks/useScroll';
import { addSignCommaFormat } from 'components/features/new-report/utils/formatter';
import {
  AssetTypesLabel,
  isNotExistDetailAssetTypes,
  toAssetTypeKey,
} from 'components/features/utils/assetType';
import {
  amountToAmountColor,
  isSymbolEmpty,
} from 'components/features/utils/util';
import { isForeignStockTypes } from 'components/features/utils/productTypes';
import { Side } from 'components/features/utils/side';
import {
  ProfitOrLossItemProps,
  PositionItemsProps,
} from './ProfitAndLossStuffing';
import {
  AboutExchangeRateModalButton,
  DataFetchingState,
  getDataFetchingState,
  isHiddenProductTypes,
} from '../PortfolioStuffing/asset/PortfolioStuffingAsset';
import { getDataFetchingTextStyle_ } from '../PortfolioRatioStuffing/PortfolioRatioStuffingList';
import { DataFetching } from '../PortfolioStuffing/asset/AssetOrganizedPortfolioItem';

const ProfitOrLossItem = ({
  assetTypeKey,
  profitOrLossAmount,
  identifier,
  name,
  link,
  profitOrLossAmountForeign,
  profitOrLossAmountForeignUnit,
}: ProfitOrLossItemProps): React.ReactElement => {
  const { pushRouting } = useRouting();

  const amountColor = amountToAmountColor(profitOrLossAmount ?? 0);
  const amount = addSignCommaFormat(profitOrLossAmount);
  const amountForeign =
    profitOrLossAmountForeign !== undefined
      ? addSignCommaFormat(profitOrLossAmountForeign, { numberOfDecimals: 2 })
      : '---';
  const unitForeign = profitOrLossAmountForeignUnit ?? '';
  const dataFetchingState: DataFetchingState = getDataFetchingState(
    identifier,
    assetTypeKey
  );
  const getDataFetchingTextStyle = getDataFetchingTextStyle_(dataFetchingState);

  // MEMO: 『類推中』はMONO-***、『類推完了』はDC-***で返却
  const isDataCooperation =
    assetTypeKey === AssetType['投資信託'] && /^MONO/.test(identifier);

  const goTradeDetail = () => {
    link && pushRouting({ path: link }, true, window.scrollY);
  };

  return (
    <>
      <div
        className={`${
          isDataCooperation ? 'text-bluegray-300' : 'text-bluegray-700'
        }`}>
        <div className="border-b border-bluegray-300">
          <div
            className={`flex items-center pt-4 ${
              link ? 'cursor-pointer' : ''
            } ${
              profitOrLossAmountForeign && profitOrLossAmountForeignUnit
                ? ''
                : 'pb-4'
            }
            `}
            onClick={(): void => goTradeDetail()}>
            <div className="flex-1 min-w-0 ml-2">
              {isDataCooperation && (
                <div className={`flex flex-row mb-2`}>
                  <DataFetching />
                </div>
              )}
              <div className="flex items-center mb-1">
                <AssetTypesLabel assetType={assetTypeKey} />
                <div className="ml-2 text-sm text-bluegray-500">
                  {identifier}
                </div>
              </div>
              <div className="w-full flex items-end leading-6">
                <div className={`text-base font-bold line-clamp-2 pr-2`}>
                  {name}
                </div>
                <div
                  className={`flex flex-row flex-shrink ml-auto font-bold ${amountColor}`}
                  style={{ whiteSpace: 'nowrap' }}>
                  <div className="text-base mr-1">{amount}</div>
                  <div className="text-xs">円</div>
                </div>
              </div>
            </div>
            {link ? (
              <div className="flex items-center ml-4 w-2">
                {link && (
                  <img
                    className="w-full"
                    src="/static/images/icon/icon-arrow-right.svg"
                    alt=""
                  />
                )}
              </div>
            ) : (
              <div className="ml-4 w-2"></div>
            )}
          </div>
          {profitOrLossAmountForeign !== undefined &&
            profitOrLossAmountForeign !== null &&
            profitOrLossAmountForeignUnit && (
              <div className="flex">
                <div
                  className="flex flex-1 items-center justify-center mr-4 pb-4"
                  onClick={e => e.stopPropagation()}>
                  <div
                    className={`${getDataFetchingTextStyle(
                      amountColor
                    )} text-sm ml-auto`}>
                    ({amountForeign} {unitForeign})
                  </div>
                  <AboutExchangeRateModalButton
                    isShowMessage={false}
                    className="ml-2"
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </>
  );
};

const SORT_METHOD = {
  up: 0,
  down: 1,
} as const;

type SortMethod = typeof SORT_METHOD[keyof typeof SORT_METHOD];

const sortProfitOrLossItem = (method: SortMethod) => (
  items: ProfitOrLossItemProps[]
): ProfitOrLossItemProps[] => {
  const profitOrLossItemPropsToOrd = (p: ProfitOrLossItemProps): number => {
    return p.profitOrLossAmount ?? 0;
  };
  return items.sort((a, b) => {
    return (
      (profitOrLossItemPropsToOrd(a) - profitOrLossItemPropsToOrd(b)) *
      (method === SORT_METHOD.up ? 1 : -1)
    );
  });
};

const positionsToProfitOrLossStuffingProps = (
  state: PositionItemsProps[],
  getIsOutOfDate: (arg: { account: string }) => boolean | null
): ProfitOrLossItemProps[] => {
  return state.map(state => {
    const assetTypeKey = toAssetTypeKey(state);
    const getLink = (state: PositionItemsProps): string | null => {
      if (
        isSymbolEmpty(state.symbol) ||
        isNotExistDetailAssetTypes(assetTypeKey)
      ) {
        return null;
      } else if (
        assetTypeKey === AssetType['投資信託'] &&
        (state.symbol === '' ||
          /^MONO/.test(state.symbol) ||
          /^DC/.test(state.symbol))
      ) {
        return `/accountdetail/${state.account}`;
      } else {
        return `/tradedetail/${state.symbol}`;
      }
    };

    return {
      assetTypeKey,
      side: assetTypeKey === AssetType['信用'] ? (state.side as Side) : null,
      brokerID: state.brokerID,
      identifier: state.symbol,
      name: state.symbolName,
      profitOrLossAmount: state.profitOrLoss,
      link: getLink(state),
      refresh: getIsOutOfDate(state)
        ? { isOutOfDate: true, account: state.account }
        : null,
      profitOrLossAmountForeign:
        assetTypeKey === AssetType['外国株式'] &&
        isForeignStockTypes(state.productType)
          ? state.profitOrLossUSD
          : undefined,
      profitOrLossAmountForeignUnit:
        assetTypeKey === AssetType['外国株式'] &&
        isForeignStockTypes(state.productType)
          ? state.currencyUnit ?? ''
          : undefined,
    };
  });
};

const Total = ({
  profitOrLosss,
}: {
  profitOrLosss: number[];
}): React.ReactElement => {
  const isProfitOrLosssEmpty = profitOrLosss.length === 0;
  const total = isProfitOrLosssEmpty
    ? null
    : profitOrLosss.reduce((acc, v) => acc + v, 0);
  const color = total === 0 || total === null ? '' : amountToAmountColor(total);

  return (
    <>
      <div className="mx-4">
        <div className="text-sm text-gray-700 leading-4 text-right">
          評価損益
        </div>
        <div className="flex items-center">
          <div className="text-base	text-gray-700 font-bold leading-4.5">
            合計
          </div>
          <div
            className={`flex justify-end leading-8 font-bold ml-auto ${color}`}>
            <div className="text-22">{addSignCommaFormat(total)}</div>
            <div className="text-xs ml-1">円</div>
          </div>
        </div>
      </div>
    </>
  );
};

const DropDown = ({
  sortMethod,
  setSortMethod,
}: {
  sortMethod: SortMethod;
  setSortMethod: (value: SortMethod) => void;
}): React.ReactElement => {
  return (
    <>
      <div className="relative ml-auto">
        <select
          className="appearance-none rounded text-xs h-8 w-100p px-2 bg-white border border-gray-400 cursor-pointer"
          value={sortMethod}
          onChange={(e): void => {
            const value = Number(e.target.value);
            if (typeof value === 'number') setSortMethod(value as SortMethod);
          }}>
          <option value={SORT_METHOD.up}>損益昇順</option>
          <option value={SORT_METHOD.down}>損益降順</option>
        </select>
        <div className="absolute pointer-events-none inset-y-0 right-0 flex items-center pr-4">
          <img
            className="w-full"
            src="/static/images/icon/icon-arrow-down.svg"
            height="6"
            width="12"
            alt=""
          />
        </div>
      </div>
    </>
  );
};

type PoitionPrios = {
  positions: PositionItemsProps[];
  getIsOutOfDate: (arg: { account: string }) => boolean | null;
  title: string;
};

export const ProfitAndLossStuffingPosition = ({
  positions: positions_,
  getIsOutOfDate,
  title,
}: PoitionPrios): React.ReactElement => {
  const [sortMethod, setSortMethod] = useState<SortMethod>(SORT_METHOD.up);

  const positions = positions_
    .filter(item => item.brokerID !== ManualBrokerID.手入力口座)
    .filter(item => !isHiddenProductTypes(item.productType))
    .sort((first, second) => first.cashMargin - second.cashMargin)
    .sort((first, second) => first.productType - second.productType)
    .reduce((acc, v): PositionItemsProps[] => {
      const last = acc[acc.length - 1];

      const isSameSymbol =
        last &&
        last.brokerID &&
        last.productType === v.productType &&
        last.cashMargin === v.cashMargin &&
        last.symbol === v.symbol &&
        last.symbolName === v.symbolName;

      if (isSameSymbol)
        return [
          ...(acc.slice(0, acc.length - 1) as PositionItemsProps[]),
          {
            ...last,
            currentAmount: (last.currentAmount ?? 0) + (v.currentAmount ?? 0),
            profitOrLoss: (last.profitOrLoss ?? 0) + (v.profitOrLoss ?? 0),
            qty: (last.qty ?? 0) + (v.qty ?? 0),
          },
        ];
      return [...acc, v];
    }, [] as PositionItemsProps[]);

  useScroll();

  return (
    <>
      <div className="p-4 pb-0 max-w-md mx-auto">
        <div className="flex items-center mb-6">
          <div className="text-lg font-bold">{title}</div>
          <DropDown setSortMethod={setSortMethod} sortMethod={sortMethod} />
        </div>
        <Total
          profitOrLosss={
            positions.map(item => item.profitOrLoss).filter(v => v) as number[]
          }
        />
      </div>
      <div className="border-t border-bluegray-300 mb-4 px-4">
        {sortProfitOrLossItem(sortMethod)(
          positionsToProfitOrLossStuffingProps(positions, getIsOutOfDate)
        ).map((p, i) => (
          <ProfitOrLossItem {...p} key={i} />
        ))}
      </div>
    </>
  );
};
